import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Filter, Edit, Trash2, History, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePrompts, useDeletePrompt } from '@/api/queries/prompts';
import { useTopics } from '@/api/queries/promptTopics';
import type { Prompt, PromptStatus } from '@/types/prompt';
import { PromptStatusEnum } from '@/types/prompt';
import { CreatePromptDialog } from '@/features/prompts/CreatePromptDialog';
import { EditPromptDialog } from '@/features/prompts/EditPromptDialog';
import { VersionHistoryDialog } from '@/features/prompts/VersionHistoryDialog';
import { TopicManagementDialog } from '@/features/prompts/TopicManagementDialog';

export default function AdminPromptsPage() {
  const { t } = useTranslation('prompts');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PromptStatus | 'all'>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState<string | null>(null);
  const [showTopicManagement, setShowTopicManagement] = useState(false);

  const queryParams = {
    ...(searchTerm && { search: searchTerm }),
    ...(statusFilter !== 'all' && { status: statusFilter as PromptStatus }),
    limit: 100,
  };

  const { data, isLoading, error } = usePrompts(queryParams);
  const { data: topicsData } = useTopics();
  const deletePrompt = useDeletePrompt();

  const getTopicName = (topicId: string) => {
    return topicsData?.topics?.find(topic => topic._id === topicId)?.name;
  };

  const handleDelete = async (promptId: string) => {
    if (window.confirm(t('messages.confirmDeletePrompt'))) {
      deletePrompt.mutate(promptId);
    }
  };

  const getStatusBadgeVariant = (status: PromptStatus) => {
    switch (status) {
      case PromptStatusEnum.ACTIVE.valueOf():
        return 'default';
      case PromptStatusEnum.DRAFT.valueOf():
        return 'secondary';
      case PromptStatusEnum.ARCHIVED.valueOf():
        return 'outline';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          {t('messages.errorLoadingPrompts', {
            message: error instanceof Error ? error.message : 'Unknown error',
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('pages.promptManagement')}</h1>
          <p className="text-gray-600">{t('pages.promptManagementDescription')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={() => setShowTopicManagement(true)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Tags className="h-4 w-4" />
            {t('topics.manageTopics')}
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {t('createPrompt')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder={t('search.searchPlaceholder')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select
                value={statusFilter}
                onValueChange={value => setStatusFilter(value as PromptStatus | 'all')}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('search.allStatus')}</SelectItem>
                  <SelectItem value={PromptStatusEnum.ACTIVE.valueOf()}>{t('active')}</SelectItem>
                  <SelectItem value={PromptStatusEnum.DRAFT.valueOf()}>{t('draft')}</SelectItem>
                  <SelectItem value={PromptStatusEnum.ARCHIVED.valueOf()}>
                    {t('archived')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prompts List */}
      {isLoading ? (
        <div className="text-center py-8">{t('messages.loadingPrompts')}</div>
      ) : (
        <div className="grid gap-4">
          {data?.prompts.map(prompt => (
            <Card
              key={prompt._id || prompt.prompt_id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/admin/prompts/${prompt.prompt_id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CardTitle className="text-lg">{prompt.name}</CardTitle>
                      <Badge variant={getStatusBadgeVariant(prompt.status)}>{prompt.status}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{prompt.description}</p>
                    {getTopicName(prompt.topic_id) && (
                      <div className="mb-2">
                        <Badge
                          variant="outline"
                          className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                        >
                          {getTopicName(prompt.topic_id)}
                        </Badge>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>
                        {t('labels.id')}: {prompt.prompt_id}
                      </span>
                      <span>
                        {t('labels.created')}:{' '}
                        {new Date(prompt.metadata.created_at).toLocaleDateString()}
                      </span>
                      <span>
                        {t('labels.updated')}:{' '}
                        {new Date(prompt.metadata.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        setShowVersionHistory(prompt.prompt_id);
                      }}
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        setEditingPrompt(prompt);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        handleDelete(prompt.prompt_id);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {prompt.query_keywords.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-gray-700">{t('keywords')}: </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {prompt.query_keywords.map((keyword, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {prompt.required_tools.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-gray-700">
                        {t('requiredTools')}:{' '}
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {prompt.required_tools.map((tool, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tool}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {data?.prompts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchTerm || statusFilter !== 'all'
                ? t('messages.noPromptsMatchCriteria')
                : t('messages.noPromptsCreateFirst')}
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <CreatePromptDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />

      {editingPrompt && (
        <EditPromptDialog
          open={!!editingPrompt}
          onOpenChange={open => !open && setEditingPrompt(null)}
          prompt={editingPrompt}
        />
      )}

      {showVersionHistory && (
        <VersionHistoryDialog
          open={!!showVersionHistory}
          onOpenChange={open => !open && setShowVersionHistory(null)}
          promptId={showVersionHistory}
        />
      )}

      <TopicManagementDialog open={showTopicManagement} onOpenChange={setShowTopicManagement} />
    </div>
  );
}
